import { createApp } from "vue";
import { createPinia } from "pinia";

import App from "./App.vue";
import router from "./router";

import OpenVue from "openvue/config";
import ToastService from "openvue/toastservice";
import Tooltip from "openvue/tooltip";

import Lara from "@openvue/themes/lara";
import Blue from "./config/themes/Blue";
import { definePreset } from "@openvue/themes";

import { components } from "./components/Components";
import "./style.css";

import axios from "axios";
import useAuth from "./composables/Auth";

import * as Sentry from "@sentry/vue";

const app = createApp(App);

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.MODE;
if (sentryDsn && environment !== 'development') {
  Sentry.init({
    app,
    dsn: sentryDsn,
    environment,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
  });
}

const { getUser, state } = useAuth();

axios.defaults.baseURL = import.meta.env.VITE_API_URL;
axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;

const authReloadKey = 'auth_reload_in_progress'

axios.interceptors.request.use((config) => {
  if (
    config.url &&
    (config.url === "/api/user" || config.url.startsWith("/api/user?"))
  ) {
    return config;
  }

  const isEmulating = state.isEmulating;
  const currentUser = state.user as { username?: string } | undefined;

  // Only add emulating flag and username parameter when emulation is active
  if (isEmulating && currentUser?.username) {
    const username = currentUser.username;

    // Initialize params if it doesn't exist
    if (!config.params) {
      config.params = {};
    }

    // Handle different param formats
    if (config.params instanceof URLSearchParams) {
      config.params.delete("username");
      config.params.delete("emulating");
      config.params.append("username", username);
      config.params.append("emulating", "true");
    } else if (typeof config.params === "object") {
      config.params.username = username;
      config.params.emulating = "true";
    }
  }
  return config;
});

axios.interceptors.response.use(
  (response) => {
    try {
      sessionStorage.removeItem(authReloadKey)
    } catch {
      // Ignore storage errors (private mode, SSR, etc.)
    }
    return response
  },
  (error) => {
    const status = error?.response?.status
    if (status === 401 || status === 419) {
      let alreadyReloading = false
      try {
        alreadyReloading = sessionStorage.getItem(authReloadKey) === 'true'
        if (!alreadyReloading) {
          sessionStorage.setItem(authReloadKey, 'true')
        }
      } catch {
        // If storage isn't available, fall back to single reload.
      }

      if (!alreadyReloading) {
        window.location.replace('/')
      }
    }
    return Promise.reject(error)
  }
)

const defaultTheme = definePreset(Lara, Blue)
// document.documentElement.classList.add('bg-[url("/light_bg.svg")]')

app.use(createPinia());
app.use(OpenVue, {
  theme: {
    preset: defaultTheme,
    options: {
      darkModeSelector: ".dark-selector",
    },
  },
});

app.use(ToastService);
app.directive("tooltip", Tooltip);

// Globally register components
components(app);

// Load user assignments so access-controlled navigation is available immediately.
getUser("", true, true).then(() => {
  app.use(router);
  app.mount("#app");
});
